// src/ui/components/WodDashboard.tsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Header from './Header';
import Divider from './tools/Divider';
import { LiveSession } from '../../core/hooks/useLiveSession';
import { Wod } from '../../core/models/Wod';
import { useDataContext } from '../../core/context/DataContext';
import { RankingItem } from '../../core/models/RankingItem';
import { useScreenResolution } from '../../core/hooks/useScreenResolution';
import 'swiper/css';
import 'swiper/css/effect-fade';
import BlocksPanel3 from './elementUI/BlocksPanel3';
import CompleteDashboardPanel from './elementUI/CompleteDashboardPanel';
import '../../App.css';

interface Props {
  boxId: number;
  liveSession: LiveSession | null;
  wod: Wod[];
  date: string;
  time: string;
}

const WodDashboard: React.FC<Props> = ({ boxId, liveSession, wod, date, time }) => {
  const { getAgentCategory } = useDataContext();
  const { getIntensityCategoryName } = useDataContext();
  const { rankingsByBlock } = useDataContext();
  const screenResolution = useScreenResolution();
  const gridRef = useRef<HTMLDivElement>(null);

  const filteredRankingsByBlock = useMemo(() => {
    const result: Record<number, Record<number, Record<number, RankingItem[]>>> = {};
    Object.entries(rankingsByBlock).forEach(([blockIdStr, byIntensity]) => {
      const blockId = Number(blockIdStr);
      const intensitiesAcc: (typeof result)[number] = {};

      Object.entries(byIntensity).forEach(([intCatStr, byRatingType]) => {
        const intCatId = Number(intCatStr);
        const ratingsAcc: (typeof intensitiesAcc)[number] = {};

        Object.entries(byRatingType).forEach(([rtStr, items]) => {
          const rtId = Number(rtStr);
          if (items?.length) ratingsAcc[rtId] = items;
        });

        if (Object.keys(ratingsAcc).length) {
          intensitiesAcc[intCatId] = ratingsAcc;
        }
      });

      if (Object.keys(intensitiesAcc).length) {
        result[blockId] = intensitiesAcc;
      }
    });
    return result;
  }, [rankingsByBlock]);

  const blockIds = useMemo(
    () => Object.keys(filteredRankingsByBlock).map(Number),
    [filteredRankingsByBlock],
  );

  useEffect(() => { }, [boxId, date, time, liveSession, rankingsByBlock]);
  useEffect(() => { }, [filteredRankingsByBlock]);

  // El manejo de zoom y viewport ahora se realiza de forma global en preventZoom.ts
  // y la compensación de fuentes en el hook useScreenResolution.

  const showBlocks = true;
  const idBlockActive = liveSession?.elementId ?? 0;
  const initialIndex = blockIds.indexOf(idBlockActive) !== -1 ? blockIds.indexOf(idBlockActive) : 0;
  const [visibleIndex, setVisibleIndex] = useState(initialIndex);

  useEffect(() => {
    if (!blockIds.length) return;
    const initialTimer = setTimeout(() => {
      const interval = setInterval(() => {
        setVisibleIndex((prev) => (prev + 1) % blockIds.length);
      }, 3000);
      return () => clearInterval(interval);
    }, 5000);
    return () => clearTimeout(initialTimer);
  }, [blockIds, idBlockActive]);

  const currentBlockId = blockIds[visibleIndex];
  const rankingsForCurrentBlock = filteredRankingsByBlock[currentBlockId] || {};

  const [year, month, day] = date.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formattedDate = `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;

  // Usar la detección de pantalla grande del hook, que ahora es más robusta
  const shouldUseTwoColumns = screenResolution.isLargeScreen;

  return (
    <div className="min-h-full h-full flex flex-col items-center justify-top text-white mx-2 pt-1 gap-1">

      <Header formattedDate={formattedDate} boxId={boxId.toString()} />
      <Divider className="h-[1px] w-full opacity-30" />

      {/* CONTENEDOR PRINCIPAL */}
      <div
        ref={gridRef}
        className={`grid gap-4 w-full px-2 overflow-y-auto custom-scrollbar ${shouldUseTwoColumns
          ? 'grid-cols-[2fr_1fr]'
          : 'grid-cols-1'
          }`}
        style={{ height: '100%' }}
      >


        <CompleteDashboardPanel wod={wod} liveSession={liveSession} boxId={boxId} />

        <BlocksPanel3
          boxId={boxId}
          date={date}
          time={time}
          showBlocks={showBlocks}
          idBlockActive={idBlockActive}
          currentBlockId={currentBlockId}
          getAgentCategory={getAgentCategory}
          getIntensityCategoryName={getIntensityCategoryName}
          rankingsForCurrentBlock={rankingsForCurrentBlock}
          onEmpty={() => { }}
        />
      </div>
    </div>
  );
};

export default React.memo(WodDashboard);